import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SlidersHorizontal, Loader2 } from "lucide-react";
import { apiPatch } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ModelContextPolicy {
  triggerTokens: number;
  targetTokens: number;
  maxOutputTokens?: number;
}
export function ModelContextSettings({
  modelId,
  name,
  policy,
}: {
  modelId: string;
  name: string;
  policy?: ModelContextPolicy;
}) {
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState(policy?.triggerTokens ?? 32000);
  const [target, setTarget] = useState(policy?.targetTokens ?? 8000);
  const [output, setOutput] = useState(policy?.maxOutputTokens?.toString() ?? "");
  const save = useMutation({
    mutationFn: () =>
      apiPatch(`/ai/models/${modelId}/context`, {
        triggerTokens: trigger,
        targetTokens: target,
        ...(output ? { maxOutputTokens: Number(output) } : {}),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ai-providers"] });
      void client.invalidateQueries({ queryKey: ["ai-project-models"] });
      setOpen(false);
    },
  });
  const invalid =
    !Number.isInteger(trigger) ||
    !Number.isInteger(target) ||
    trigger < 4000 ||
    trigger > 200000 ||
    target < 1000 ||
    target > 40000 ||
    target > trigger / 2 ||
    (!!output && (!Number.isInteger(Number(output)) || Number(output) < 512 || Number(output) > 65536));
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs shrink-0"
        aria-label={`${fr ? "Contexte" : "Context"} · ${name}`}
        onClick={() => {
          setTrigger(policy?.triggerTokens ?? 32000);
          setTarget(policy?.targetTokens ?? 8000);
          setOutput(policy?.maxOutputTokens?.toString() ?? "");
          setOpen(true);
        }}
      >
        <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
        {fr ? "Contexte" : "Context"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {fr ? "Contexte et sortie du modèle" : "Model context and output"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm font-medium break-words">{name}</p>
          <p className="text-sm text-foreground-muted">
            {fr
              ? "L’IA résume automatiquement l’historique pour conserver l’objectif, les consignes et les actions déjà réalisées. La conversation complète reste visible. Les derniers messages restent conservés et peuvent dépasser la cible. Ces seuils concernent l’historique estimé ; les outils et instructions du modèle s’ajoutent au contexte."
              : "The AI automatically summarizes history, preserving the goal, instructions and completed actions. The complete conversation stays visible. Recent messages remain available and may exceed the target. Thresholds use estimated history tokens; model instructions and tools add to context."}
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`trigger-${modelId}`}>
                {fr ? "Déclencher à" : "Trigger at"}
              </Label>
              <Input
                id={`trigger-${modelId}`}
                type="number"
                min={4000}
                max={200000}
                step={1000}
                value={trigger}
                onChange={(event) => setTrigger(Number(event.target.value))}
              />
              <p className="text-xs text-foreground-muted">tokens</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor={`target-${modelId}`}>
                {fr ? "Viser après résumé" : "Target after summary"}
              </Label>
              <Input
                id={`target-${modelId}`}
                type="number"
                min={1000}
                max={40000}
                step={1000}
                value={target}
                onChange={(event) => setTarget(Number(event.target.value))}
              />
              <p className="text-xs text-foreground-muted">tokens</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`output-${modelId}`}>{fr ? "Limite de sortie par réponse" : "Output tokens per response"}</Label>
            <Input id={`output-${modelId}`} type="number" min={512} max={65536} value={output} placeholder={fr ? "Automatique selon le modèle" : "Automatic for this model"} onChange={event => setOutput(event.target.value)} />
            <p className="text-xs text-foreground-muted">{fr ? "Laissez vide pour la politique du modèle. Les grands travaux continuent par petits lots ; les réponses tronquées sont reprises automatiquement." : "Leave blank for the model policy. Large requests continue in small batches; truncated responses retry automatically."}</p>
          </div>
          {invalid && (
            <p className="text-xs text-destructive">
              {fr
                ? "La cible doit être entre 1 000 et 40 000 tokens, au maximum la moitié du seuil. La sortie doit être entre 512 et 65 536 tokens."
                : "Target must be 1,000–40,000 tokens, at most half the trigger. Output must be 512–65,536 tokens."}
            </p>
          )}
          {save.error && (
            <p role="alert" className="text-sm text-destructive">
              {save.error.message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {fr ? "Annuler" : "Cancel"}
            </Button>
            <Button
              disabled={invalid || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              {fr ? "Enregistrer" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
